<template>
  <div
    class="accruals-estimation-vendor-select"
    data-testid="accruals-configuration-page__vendor-select"
  >
    <v-menu
      v-model="menuOpen"
      :close-on-content-click="false"
      offset-y
      nudge-bottom="4"
      :max-width="menuWidth"
      :min-width="menuWidth"
      content-class="accruals-estimation-vendor-select__menu"
    >
      <template #activator="{ on, attrs }">
        <div
          ref="activator"
          class="accruals-estimation-vendor-select__field"
          role="button"
          tabindex="0"
          data-testid="accruals-configuration-page__vendor-select-field"
          v-bind="attrs"
          v-on="on"
        >
          <div class="accruals-estimation-vendor-select__chips">
            <span
              v-for="vendor in selectedVendors"
              :key="vendor.id"
              class="accruals-estimation-vendor-select__chip"
            >
              <span>{{ formatVendorLabel(vendor) }}</span>
              <button
                type="button"
                class="accruals-estimation-vendor-select__chip-remove"
                aria-label="Remove vendor"
                @click.stop="removeVendor(vendor.id)"
              >
                <img :src="assets.ICON_CHIP_REMOVE" alt="" />
              </button>
            </span>
            <span
              class="accruals-estimation-vendor-select__placeholder"
              :class="{ 'accruals-estimation-vendor-select__placeholder--inline': selectedVendors.length > 0 }"
            >
              Search vendors...
            </span>
          </div>
          <img
            :src="assets.ICON_CHEVRON_DOWN"
            alt=""
            class="accruals-estimation-vendor-select__chevron"
            aria-hidden="true"
          />
        </div>
      </template>

      <div class="accruals-estimation-vendor-select__panel">
        <div class="accruals-estimation-vendor-select__search-wrap">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="accruals-estimation-vendor-select__search"
            placeholder="Search vendors..."
            data-testid="accruals-configuration-page__vendor-search"
            @click.stop
            @mousedown.stop
          />
        </div>
        <div class="accruals-estimation-vendor-select__options">
          <div
            v-if="loading"
            class="accruals-estimation-vendor-select__empty"
          >
            Loading vendors…
          </div>
          <div
            v-else-if="!filteredVendors.length"
            class="accruals-estimation-vendor-select__empty"
          >
            No vendors found. Sync Vendor Master, then open this list again.
          </div>
          <template v-else>
            <div
              v-for="vendor in filteredVendors"
              :key="vendor.id"
              class="accruals-estimation-vendor-select__option"
              :data-testid="`accruals-configuration-page__vendor-option--${vendor.id}`"
            >
            <v-checkbox
              :input-value="isSelected(vendor.id)"
              hide-details
              :ripple="false"
              color="caccounting"
              class="accruals-estimation-vendor-select__checkbox pa-0 ma-0"
              :data-testid="`accruals-configuration-page__vendor-checkbox--${vendor.id}`"
              @change="onVendorChange(vendor.id, $event)"
            >
              <template #label>
                <span class="accruals-estimation-vendor-select__option-content">
                  <span class="accruals-estimation-vendor-select__option-name">{{ vendor.name }}</span>
                  <span
                    v-if="vendor.id !== allVendorsId"
                    class="accruals-estimation-vendor-select__option-id"
                  >{{ vendor.id }}</span>
                </span>
              </template>
            </v-checkbox>
            </div>
          </template>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
import { ACCRUALS_CONFIG_ASSETS } from '../accrualsConfigurationConstants';
import {
  ESTIMATION_ALL_VENDORS_ID,
  formatVendorLabel,
} from '../accrualsEstimationRulesConstants';

const ALL_VENDORS_OPTION = { id: ESTIMATION_ALL_VENDORS_ID, name: 'All vendors' };

export default {
  name: 'AccrualsEstimationVendorSelect',
  props: {
    value: { type: Array, default: () => [] },
  },
  data() {
    return {
      assets: ACCRUALS_CONFIG_ASSETS,
      menuOpen: false,
      menuWidth: 0,
      searchQuery: '',
      vendorOptions: [ALL_VENDORS_OPTION],
      allVendorsId: ESTIMATION_ALL_VENDORS_ID,
      loading: false,
    };
  },
  computed: {
    selectedVendors() {
      return (this.value || []).map((id) => (
        this.vendorOptions.find((vendor) => vendor.id === id) || { id, name: id }
      ));
    },
    filteredVendors() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.vendorOptions;
      return this.vendorOptions.filter((vendor) => (
        vendor.name.toLowerCase().includes(query)
        || vendor.id.toLowerCase().includes(query)
      ));
    },
  },
  watch: {
    menuOpen(isOpen) {
      if (isOpen) {
        this.updateMenuWidth();
        if (!this.loading && this.vendorOptions.length <= 1) {
          this.loadVendors();
        }
        this.$nextTick(() => {
          const input = this.$refs.searchInput;
          if (input) input.focus();
        });
        return;
      }
      this.searchQuery = '';
    },
  },
  created() {
    this.loadVendors();
  },
  methods: {
    formatVendorLabel,
    async loadVendors() {
      const projectId = this.$route.params.id;
      if (!projectId) return;
      this.loading = true;
      try {
        const rows = await this.$store.dispatch('accruals/loadVendorMasterOptions', {
          projectId,
        });
        this.vendorOptions = [ALL_VENDORS_OPTION].concat(rows || []);
      } catch (err) {
        console.log('[AccrualsEstimationVendorSelect]: loadVendors failed', err);
        this.vendorOptions = [ALL_VENDORS_OPTION];
      } finally {
        this.loading = false;
      }
    },
    updateMenuWidth() {
      const field = this.$refs.activator;
      if (!field) return;
      this.menuWidth = Math.max(field.getBoundingClientRect().width, 280);
    },
    isSelected(vendorId) {
      return (this.value || []).includes(vendorId);
    },
    onVendorChange(vendorId, selected) {
      if (vendorId === ESTIMATION_ALL_VENDORS_ID) {
        this.$emit('input', selected ? [ESTIMATION_ALL_VENDORS_ID] : []);
        return;
      }
      const next = new Set((this.value || []).filter((id) => id !== ESTIMATION_ALL_VENDORS_ID));
      if (selected) next.add(vendorId);
      else next.delete(vendorId);
      this.$emit('input', Array.from(next));
    },
    removeVendor(vendorId) {
      this.$emit('input', (this.value || []).filter((id) => id !== vendorId));
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimationVendorSelect.scss';
</style>

<style lang="scss">
@import './AccrualsEstimationVendorSelectMenu.scss';
</style>
